import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="flex items-center gap-2 text-[hsl(20,10%,40%)] hover:text-[hsl(20,10%,25%)] mb-8">
          <ArrowLeft className="w-4 h-4" />Volver
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-8 sm:p-12 prose prose-gray max-w-none">
          <h1 className="font-heading font-bold text-3xl text-gray-900 mb-2">Política de Privacidad</h1>
          <p className="text-sm text-gray-500 mb-8">Última actualización: mayo de 2026</p>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">1. Responsable del tratamiento</h2>
            <p className="text-gray-600 leading-relaxed">
              En cumplimiento del Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo (RGPD) y de la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD), te informamos de que los responsables del tratamiento de tus datos personales son:
            </p>
            <div className="mt-4 p-4 bg-[hsl(35,25%,95%)] rounded-xl text-gray-600 space-y-3">
              <div>
                <p><strong>Cotitular:</strong> Alba Pérez Rodríguez</p>
                <p><strong>NIF:</strong> 72596176B</p>
              </div>
              <div>
                <p><strong>Cotitular:</strong> Manuel Arroyo González</p>
                <p><strong>NIF:</strong> 76269479F</p>
              </div>
              <div className="pt-1">
                <p><strong>Denominación comercial:</strong> Partykatu</p>
                <p><strong>Dominio:</strong> partykatu.com</p>
                <p><strong>Email de contacto:</strong> infopartykatu@gmail.com</p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">2. Datos que recopilamos</h2>
            <p className="text-gray-600 leading-relaxed mb-4">Recopilamos los siguientes datos personales cuando utilizas Partykatu:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Datos de registro:</strong> nombre, dirección de correo electrónico y contraseña (almacenada de forma cifrada).</li>
              <li><strong>Datos de perfil:</strong> edad, género, ciudad, barrio, fotografía de perfil, ocupación, biografía, presupuesto, preferencias de convivencia, hábitos de vida y actividades de ocio.</li>
              <li><strong>Datos de uso:</strong> intereses mostrados hacia otros usuarios, mensajes intercambiados a través del chat interno, y actividad general en la plataforma.</li>
              <li><strong>Datos técnicos:</strong> dirección IP, tipo de navegador y dispositivo, registros de acceso y cookies técnicas necesarias para el funcionamiento del servicio.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">3. Finalidad y base jurídica del tratamiento</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-gray-600 border-collapse">
                <thead>
                  <tr className="bg-[hsl(35,25%,95%)]">
                    <th className="text-left p-3 font-semibold text-gray-900 border border-[hsl(35,20%,85%)]">Finalidad</th>
                    <th className="text-left p-3 font-semibold text-gray-900 border border-[hsl(35,20%,85%)]">Base jurídica</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Gestionar tu cuenta y permitirte acceder al servicio', 'Ejecución del contrato (art. 6.1.b RGPD)'],
                    ['Mostrar tu perfil a otros usuarios y facilitar la búsqueda de compañero/a de piso', 'Ejecución del contrato (art. 6.1.b RGPD)'],
                    ['Enviar comunicaciones sobre el servicio (cambios, incidencias)', 'Interés legítimo (art. 6.1.f RGPD)'],
                    ['Gestionar denuncias y mantener la seguridad de la plataforma', 'Obligación legal e interés legítimo (art. 6.1.c y 6.1.f RGPD)'],
                    ['Mejorar el servicio mediante análisis de uso anonimizado', 'Interés legítimo (art. 6.1.f RGPD)'],
                  ].map(([fin, base], i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-[hsl(35,25%,97%)]'}>
                      <td className="p-3 border border-[hsl(35,20%,85%)]">{fin}</td>
                      <td className="p-3 border border-[hsl(35,20%,85%)]">{base}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">4. Conservación de los datos</h2>
            <p className="text-gray-600 leading-relaxed">
              Conservaremos tus datos personales mientras mantengas una cuenta activa en Partykatu. Una vez que elimines tu cuenta, procederemos a eliminar tus datos en un plazo máximo de <strong>30 días</strong>, salvo aquellos que debamos conservar por obligaciones legales (por ejemplo, registros de facturación durante 5 años según la normativa fiscal española).
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              Los mensajes del chat se eliminarán de forma inmediata al eliminar la cuenta.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">5. Comunicación de datos a terceros</h2>
            <p className="text-gray-600 leading-relaxed mb-4">Tus datos no se cederán a terceros salvo en los siguientes casos:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Cloudinary:</strong> almacenamiento de imágenes de perfil y portada (servidor en EE.UU., con garantías adecuadas mediante Cláusulas Contractuales Tipo de la UE).</li>
              <li><strong>MongoDB Atlas:</strong> base de datos donde se almacena la información de los usuarios.</li>
              <li><strong>Render:</strong> proveedor de infraestructura donde se ejecuta el backend de la aplicación.</li>
              <li><strong>Vercel:</strong> plataforma de alojamiento del frontend de la aplicación.</li>
              <li><strong>Obligación legal:</strong> cuando sea requerido por autoridades competentes conforme a la legislación vigente.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Todos los proveedores han suscrito los correspondientes contratos de encargado del tratamiento y ofrecen garantías suficientes conforme al RGPD.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">6. Transferencias internacionales</h2>
            <p className="text-gray-600 leading-relaxed">
              Algunos de nuestros proveedores (Cloudinary, Render, Vercel) pueden procesar datos fuera del Espacio Económico Europeo (EEE), principalmente en Estados Unidos. En todos los casos, estas transferencias se realizan con las garantías adecuadas conforme al artículo 46 del RGPD, mediante Cláusulas Contractuales Tipo aprobadas por la Comisión Europea o al amparo del Data Privacy Framework UE-EE.UU.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">7. Tus derechos</h2>
            <p className="text-gray-600 leading-relaxed mb-4">En virtud del RGPD, tienes los siguientes derechos sobre tus datos personales:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Acceso:</strong> obtener confirmación de si tratamos tus datos y acceder a ellos.</li>
              <li><strong>Rectificación:</strong> corregir datos inexactos o incompletos.</li>
              <li><strong>Supresión («derecho al olvido»):</strong> solicitar la eliminación de tus datos cuando ya no sean necesarios.</li>
              <li><strong>Oposición:</strong> oponerte al tratamiento basado en interés legítimo.</li>
              <li><strong>Limitación:</strong> solicitar que suspendamos el tratamiento en determinadas circunstancias.</li>
              <li><strong>Portabilidad:</strong> recibir tus datos en formato estructurado y legible por máquina.</li>
              <li><strong>Retirada del consentimiento:</strong> cuando el tratamiento se base en tu consentimiento, puedes retirarlo en cualquier momento sin que ello afecte a la licitud del tratamiento previo.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Para ejercer cualquiera de estos derechos, escríbenos a <a href="mailto:infopartykatu@gmail.com" className="text-[hsl(144,18%,45%)] hover:underline">infopartykatu@gmail.com</a> indicando tu nombre, el derecho que deseas ejercer y adjuntando una copia de tu DNI u otro documento acreditativo.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              También tienes derecho a presentar una reclamación ante la <strong>Agencia Española de Protección de Datos (AEPD)</strong> en <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[hsl(144,18%,45%)] hover:underline">www.aepd.es</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">8. Seguridad</h2>
            <p className="text-gray-600 leading-relaxed">
              Aplicamos medidas técnicas y organizativas apropiadas para proteger tus datos personales frente a accesos no autorizados, pérdida, destrucción o divulgación accidental. Entre otras medidas, utilizamos cifrado HTTPS en todas las comunicaciones, almacenamiento cifrado de contraseñas mediante bcrypt, tokens de acceso con caducidad corta, y acceso restringido a la base de datos.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">9. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              Partykatu utiliza únicamente cookies técnicas estrictamente necesarias para el funcionamiento del servicio (gestión de sesión y autenticación). No utilizamos cookies de seguimiento, publicidad ni análisis de terceros. Al ser cookies técnicas necesarias, no requieren tu consentimiento previo conforme a la LSSI y a las directrices de la AEPD.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">10. Menores de edad</h2>
            <p className="text-gray-600 leading-relaxed">
              Partykatu es un servicio dirigido exclusivamente a personas mayores de 18 años. No recopilamos conscientemente datos de menores de edad. Si detectamos que un usuario es menor de 18 años, procederemos a eliminar su cuenta y datos de forma inmediata.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">11. Cambios en esta política</h2>
            <p className="text-gray-600 leading-relaxed">
              Podemos actualizar esta Política de Privacidad periódicamente. Te notificaremos cualquier cambio significativo mediante un aviso en la aplicación o por correo electrónico. La fecha de última actualización siempre estará visible al inicio de este documento.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl text-gray-900 mb-4">12. Contacto</h2>
            <p className="text-gray-600 leading-relaxed">
              Para cualquier consulta relacionada con esta Política de Privacidad o con el tratamiento de tus datos personales, puedes contactarnos en:
            </p>
            <div className="mt-3 p-4 bg-[hsl(35,25%,95%)] rounded-xl text-gray-600">
              <p><strong>Email:</strong> <a href="mailto:infopartykatu@gmail.com" className="text-[hsl(144,18%,45%)] hover:underline">infopartykatu@gmail.com</a></p>
              <p className="mt-1"><strong>Web:</strong> <a href="https://partykatu.com" className="text-[hsl(144,18%,45%)] hover:underline">partykatu.com</a></p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
